import type { Lang } from '@/i18n/locale';

/** Nine additional approved lower-body labels. Existing fifteen and Upper16 labels are preserved. */
export const LOWER_FITNESS_LABEL_IDS = [
  "free-legs-cadeira-extensora",
  "free-legs-stiff",
  "free-legs-bulgaro",
  "free-legs-panturrilha-sentado",
  "free-glutes-hip-thrust",
  "free-glutes-glute-bridge",
  "free-glutes-sumo",
  "free-glutes-bom-dia",
  "free-glutes-stiff-unilateral"
] as const;
export type LowerFitnessLabelId = typeof LOWER_FITNESS_LABEL_IDS[number];
export const LOWER_FITNESS_EXERCISE_LABELS = {
  "EN": {
    "free-legs-cadeira-extensora": "Seated Machine Leg Extension",
    "free-legs-stiff": "Soft-knee Barbell Stiff-leg Deadlift",
    "free-legs-bulgaro": "Dumbbell Bulgarian Split Squat",
    "free-legs-panturrilha-sentado": "Bilateral Seated-machine Calf Raise",
    "free-glutes-hip-thrust": "Bench-supported Barbell Hip Thrust",
    "free-glutes-glute-bridge": "Bodyweight Floor Glute Bridge",
    "free-glutes-sumo": "Bodyweight Sumo Squat",
    "free-glutes-bom-dia": "Barbell Good Morning",
    "free-glutes-stiff-unilateral": "Single-leg Stiff Hinge with Two Dumbbells"
  },
  "PT-BR": {
    "free-legs-cadeira-extensora": "Cadeira extensora bilateral",
    "free-legs-stiff": "Stiff com barra e joelhos levemente flexionados",
    "free-legs-bulgaro": "Agachamento búlgaro com halteres",
    "free-legs-panturrilha-sentado": "Panturrilha sentada na máquina (bilateral)",
    "free-glutes-hip-thrust": "Elevação de quadril com barra e costas no banco",
    "free-glutes-glute-bridge": "Ponte de glúteos no chão sem peso",
    "free-glutes-sumo": "Agachamento sumô sem peso",
    "free-glutes-bom-dia": "Bom dia com barra nas costas",
    "free-glutes-stiff-unilateral": "Stiff unilateral com dois halteres"
  },
  "NL": {
    "free-legs-cadeira-extensora": "Zittende knie-extensie op machine",
    "free-legs-stiff": "Stiff-leg deadlift met halterstang en licht gebogen knieën",
    "free-legs-bulgaro": "Bulgaarse split squat met dumbbells",
    "free-legs-panturrilha-sentado": "Zittend tweebenig kuitheffen op machine",
    "free-glutes-hip-thrust": "Hip thrust met halterstang en banksteun",
    "free-glutes-glute-bridge": "Glute bridge op de vloer zonder gewicht",
    "free-glutes-sumo": "Sumosquat zonder gewicht",
    "free-glutes-bom-dia": "Good morning met halterstang",
    "free-glutes-stiff-unilateral": "Eenbenige stiff-leg hinge met twee dumbbells"
  },
  "DE": {
    "free-legs-cadeira-extensora": "Beinstrecken an der Maschine im Sitzen",
    "free-legs-stiff": "Stiff-Leg-Kreuzheben mit Langhantel und leicht gebeugten Knien",
    "free-legs-bulgaro": "Bulgarischer Split Squat mit Kurzhanteln",
    "free-legs-panturrilha-sentado": "Beidbeiniges Wadenheben an der Sitzmaschine",
    "free-glutes-hip-thrust": "Langhantel-Hip-Thrust mit Bankstütze",
    "free-glutes-glute-bridge": "Glute Bridge am Boden ohne Zusatzgewicht",
    "free-glutes-sumo": "Sumokniebeuge ohne Zusatzgewicht",
    "free-glutes-bom-dia": "Good Morning mit Langhantel",
    "free-glutes-stiff-unilateral": "Einbeiniges Stiff-Leg-Heben mit zwei Kurzhanteln"
  },
  "IT": {
    "free-legs-cadeira-extensora": "Leg extension da seduti alla macchina",
    "free-legs-stiff": "Stacco a gambe semitese con bilanciere",
    "free-legs-bulgaro": "Squat bulgaro con manubri",
    "free-legs-panturrilha-sentado": "Calf raise bilaterale da seduti alla macchina",
    "free-glutes-hip-thrust": "Hip thrust con bilanciere e appoggio sulla panca",
    "free-glutes-glute-bridge": "Ponte glutei a terra a corpo libero",
    "free-glutes-sumo": "Squat sumo a corpo libero",
    "free-glutes-bom-dia": "Good morning con bilanciere",
    "free-glutes-stiff-unilateral": "Stacco su una gamba con due manubri"
  },
  "FR": {
    "free-legs-cadeira-extensora": "Extension des genoux à la machine assise",
    "free-legs-stiff": "Soulevé de terre jambes légèrement fléchies à la barre",
    "free-legs-bulgaro": "Fente bulgare aux haltères",
    "free-legs-panturrilha-sentado": "Élévation bilatérale des mollets à la machine assise",
    "free-glutes-hip-thrust": "Hip thrust à la barre avec appui sur banc",
    "free-glutes-glute-bridge": "Pont fessier au sol au poids du corps",
    "free-glutes-sumo": "Squat sumo au poids du corps",
    "free-glutes-bom-dia": "Good morning à la barre",
    "free-glutes-stiff-unilateral": "Soulevé de terre sur une jambe avec deux haltères"
  },
  "PL": {
    "free-legs-cadeira-extensora": "Prostowanie nóg na maszynie siedząc",
    "free-legs-stiff": "Martwy ciąg ze sztangą z lekko ugiętymi kolanami",
    "free-legs-bulgaro": "Przysiad bułgarski z hantlami",
    "free-legs-panturrilha-sentado": "Obunóż wspięcia na palce na maszynie siedząc",
    "free-glutes-hip-thrust": "Hip thrust ze sztangą i podparciem na ławce",
    "free-glutes-glute-bridge": "Most biodrowy na podłodze bez obciążenia",
    "free-glutes-sumo": "Przysiad sumo bez obciążenia",
    "free-glutes-bom-dia": "Good morning ze sztangą",
    "free-glutes-stiff-unilateral": "Martwy ciąg na jednej nodze z dwoma hantlami"
  },
  "BG": {
    "free-legs-cadeira-extensora": "Разгъване на колене на машина от седеж",
    "free-legs-stiff": "Тяга с щанга с леко свити колене",
    "free-legs-bulgaro": "Български клек с дъмбели",
    "free-legs-panturrilha-sentado": "Двустранно повдигане на пръсти на седяща машина",
    "free-glutes-hip-thrust": "Хип тръст с щанга и опора на пейка",
    "free-glutes-glute-bridge": "Глутеус мост на пода със собствено тегло",
    "free-glutes-sumo": "Сумо клек със собствено тегло",
    "free-glutes-bom-dia": "Добро утро с щанга на гърба",
    "free-glutes-stiff-unilateral": "Тяга на един крак с два дъмбела"
  },
  "ES": {
    "free-legs-cadeira-extensora": "Extensión de rodillas sentado en máquina",
    "free-legs-stiff": "Peso muerto rígido con barra y rodillas ligeramente flexionadas",
    "free-legs-bulgaro": "Sentadilla búlgara con mancuernas",
    "free-legs-panturrilha-sentado": "Elevación bilateral de gemelos sentado en máquina",
    "free-glutes-hip-thrust": "Hip thrust con barra y apoyo en banco",
    "free-glutes-glute-bridge": "Puente de glúteos en el suelo sin carga",
    "free-glutes-sumo": "Sentadilla sumo sin carga",
    "free-glutes-bom-dia": "Buenos días con barra",
    "free-glutes-stiff-unilateral": "Bisagra rígida a una pierna con dos mancuernas"
  }
} satisfies Record<Lang, Record<LowerFitnessLabelId, string>>;
